import {
  AssessmentSubjectType,
  type DynamicAssessmentResponse,
  type DynamicAssessmentResponseItem,
  type SaveDynamicAssessmentResponseItem,
} from '../types/dynamicAssessment';

/**
 * Builds a field-code lookup from saved dynamic assessment responses.
 */
export function responsesToMap(
  responses: DynamicAssessmentResponseItem[]
): Record<string, DynamicAssessmentResponseItem> {
  return responses.reduce<Record<string, DynamicAssessmentResponseItem>>(
    (map, item) => {
      map[item.fieldCode] = item;
      return map;
    },
    {}
  );
}

/**
 * Extracts the display/save value for a response item based on populated fields.
 */
export function getResponseValue(
  item: DynamicAssessmentResponseItem | undefined
): string | number | boolean | undefined {
  if (!item) {
    return undefined;
  }

  if (item.optionValue != null) {
    return item.optionValue;
  }

  if (item.numericValue != null) {
    return item.numericValue;
  }

  if (item.booleanValue != null) {
    return item.booleanValue;
  }

  if (item.textValue != null) {
    return item.textValue;
  }

  return item.jsonValue;
}

/**
 * Converts a keyed form value map into save payload items.
 */
export function toSaveResponseItems(
  values: Record<string, string | number | boolean | null | undefined>
): SaveDynamicAssessmentResponseItem[] {
  return Object.entries(values)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([fieldCode, value]) => {
      const item: SaveDynamicAssessmentResponseItem = { fieldCode };

      if (typeof value === 'boolean') {
        item.booleanValue = value;
      } else if (typeof value === 'number') {
        // Rating/select options are integers; other numeric fields use numericValue.
        if (Number.isInteger(value)) {
          item.optionValue = value;
        } else {
          item.numericValue = value;
        }
      } else {
        item.textValue = String(value);
      }

      return item;
    });
}

/**
 * Merges schema defaults with any saved instance responses.
 */
export function mergeResponsesWithDefaults(
  saved: DynamicAssessmentResponse | undefined,
  defaults: Record<string, string | number | boolean | undefined>
): Record<string, string | number | boolean | undefined> {
  const merged = { ...defaults };
  const map = responsesToMap(saved?.responses ?? []);

  for (const [fieldCode, item] of Object.entries(map)) {
    const value = getResponseValue(item);
    if (value !== undefined) {
      merged[fieldCode] = value;
    }
  }

  return merged;
}

/**
 * Default assessment type code when the caller omits one.
 */
export function resolveAssessmentTypeCode(code?: string): string {
  return code?.trim() || 'CAPABILITY_MATURITY';
}

/**
 * Convenience builder for sub-capability subject requests.
 */
export function buildSubCapabilitySubject(
  userId: number,
  projectId: number,
  subCapabilityId: number,
  assessmentTypeCode?: string
) {
  return {
    userId,
    projectId,
    subjectType: AssessmentSubjectType.SubCapability,
    subjectId: subCapabilityId,
    assessmentTypeCode: resolveAssessmentTypeCode(assessmentTypeCode),
  };
}
